import DramoxImage from 'components/DramoxImage';
import useTranslation from 'next-translate/useTranslation';
import { useEffect, useState } from 'react';
import { useAuth } from '../../context';
import { Namespace } from '../../enums/namespaces';
import { Permalink, truncateTitle } from '../../helpers';
import { PlayBase, PlayState } from '../../typings/api';
import AppLink from '../AppLink/AppLink';
import Select from '../Select/Select';

interface Props {
  headline: string;
  plays: PlayBase[];
  index?: number;
  isSortable?: boolean;
  emptyState?: boolean;
}

enum SortType {
  DateDesc = 'dateAsc',
  DateAsc = 'dateDesc',
  Alpha = 'alphabeticallyDesc',
}

const CategorySectionPlaylist = ({
  headline,
  plays,
  index,
  isSortable = false,
  emptyState = false,
}: Props): JSX.Element => {
  const { lang, t } = useTranslation(Namespace.Common);
  const { user } = useAuth();
  const [sortedPlays, setSortedPlays] = useState([]);

  useEffect(() => {
    onSortChange(SortType.DateDesc);
  }, []);

  const onSortChange = (sort: SortType) => {
    const tempPlays = [...plays];
    if (sort === SortType.Alpha) {
      tempPlays.sort((a, b) => a.title.localeCompare(b.title));
      setSortedPlays(tempPlays);
    } else if (sort === SortType.DateAsc || sort === SortType.DateDesc) {
      tempPlays.sort((a, b) => {
        const first = new Date(a.watchlist_added);
        const second = new Date(b.watchlist_added);
        if (first.getTime() < second.getTime()) {
          return sort === SortType.DateAsc ? -1 : 1;
        } else if (first.getTime() > second.getTime()) {
          return sort === SortType.DateAsc ? 1 : -1;
        } else {
          return 0;
        }
      });
      setSortedPlays(tempPlays);
    }
  };

  return (
    <div className={`category-section category-playlist ${!index || index === 0 ? 'first' : ''}`}>
      <div className={`play-list-wrapper ${isSortable ? 'with-sort' : ''}`}>
        <div className="sort-wrapper">
          <h1>{headline}</h1>
          {isSortable ? (
            <div className={`sort-option `}>
              <span>{t('sort_from')}:</span>
              <Select
                className="sort-select"
                name=""
                onChange={(val) => {
                  onSortChange(val as SortType);
                }}
                options={[
                  { value: SortType.DateDesc, label: `${t('sort_date_desc')}` },
                  { value: SortType.DateAsc, label: `${t('sort_date_asc')}` },
                  { value: SortType.Alpha, label: `${t('sort_alphabetically_desc')}` },
                ]}
                defaultValue={SortType.DateDesc}
              />
            </div>
          ) : null}
        </div>
        {plays?.length === 0 && emptyState ? (
          <div className="empty-list">
            <div className="icon"></div>
            <h2>{t('add_first_play')}</h2>
            <p>{t('how_add')}</p>
            <AppLink href={Permalink.root(user, lang)}>
              <a>{t('check_library')}</a>
            </AppLink>
          </div>
        ) : (
          <ul className="play-list">
            {sortedPlays?.map((play, i) => (
              // onClick={() => playsGEvent(item.permalink)}
              <li key={i}>
                <AppLink href={Permalink.play(play, lang)}>
                  <a tabIndex={-1}>
                    {play.state === PlayState.PROMOTED ? <p className="coming-soon">{t('coming_soon')}</p> : null}
                    <DramoxImage
                      placeholderClass="image-wrapper-for-playlist"
                      src={play.poster_url}
                      alt={play.title}
                      width={256}
                      height={144}
                      quality={90}
                    />
                    <div>
                      <h3>{truncateTitle(play.title)}</h3>
                      <p>{play?.theatre?.name}</p>
                    </div>
                  </a>
                </AppLink>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default CategorySectionPlaylist;
